import React, {FC, useState, useEffect} from 'react'
import {Modal, Button, Form} from 'react-bootstrap'

interface FolderModalProps {
  open: boolean
  handleClose: () => void
  existingFolders: string[]
  currentFolder: string
  onSelectFolder: (folderPath: string) => void
  sceneName: string
}

const FolderModal: FC<FolderModalProps> = ({
  open,
  handleClose,
  existingFolders,
  currentFolder,
  onSelectFolder,
  sceneName,
}) => {
  const [selectedFolder, setSelectedFolder] = useState('')
  const [newFolderName, setNewFolderName] = useState('')
  const [isCreatingNew, setIsCreatingNew] = useState(false)

  useEffect(() => {
    if (open) {
      setSelectedFolder('')
      setNewFolderName('')
      // Default to creating new folder if there are no existing folders
      setIsCreatingNew(existingFolders.length === 0)
    }
  }, [open, existingFolders.length])

  const handleSubmit = () => {
    if (isCreatingNew && newFolderName.trim()) {
      // Create new folder path
      const folderPath = currentFolder 
        ? `${currentFolder}/${newFolderName.trim()}`
        : newFolderName.trim()
      onSelectFolder(folderPath)
    } else if (selectedFolder) {
      onSelectFolder(selectedFolder)
    }
    handleClose()
  }

  const isValid = () => {
    if (isCreatingNew) {
      return newFolderName.trim().length > 0
    }
    return selectedFolder.length > 0
  }

  return (
    <Modal show={open} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Add "{sceneName}" to Folder</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {currentFolder && (
          <div className="mb-3 p-2 bg-light rounded">
            <small className="text-muted">Current location: {currentFolder || 'Root'}</small>
          </div>
        )}
        
        <Form>
          {existingFolders.length > 0 && (
            <Form.Group className="mb-3">
              <Form.Check
                type="radio"
                label="Select existing folder"
                name="folderOption"
                id="select-existing"
                checked={!isCreatingNew}
                onChange={() => setIsCreatingNew(false)}
              />
              {!isCreatingNew && (
                <Form.Select
                  className="mt-2"
                  value={selectedFolder}
                  onChange={(e) => setSelectedFolder(e.target.value)}
                  autoFocus
                >
                  <option value="">Select a folder...</option>
                  {existingFolders.map((folder, idx) => (
                    <option key={idx} value={folder}>
                      {folder}
                    </option>
                  ))}
                </Form.Select>
              )}
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Check
              type="radio"
              label="Create new folder"
              name="folderOption"
              id="create-new"
              checked={isCreatingNew}
              onChange={() => setIsCreatingNew(true)}
            />
            {isCreatingNew && (
              <Form.Control
                type="text"
                placeholder="Enter folder name"
                className="mt-2"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                autoFocus
              />
            )}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={!isValid()}>
          Add to Folder
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default FolderModal

